import { useCallback, useEffect, useState } from "react";
import {
  Users, UserPlus, ShieldCheck, Eye, Copy, Check, X, Trash2, KeyRound, Loader2, Clock,
} from "lucide-react";
import { usersApi, formatApiError, fmtTimeAgo } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["admin", "read-only"];

const RoleBadge = ({ role }) => (
  <span
    className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${
      role === "admin"
        ? "border-octopi-purple/60 text-octopi-purple bg-octopi-purple/10"
        : "border-nexus-cyan/50 text-nexus-cyan bg-nexus-cyan/5"
    }`}
  >
    {role}
  </span>
);

const StatusBadge = ({ status }) => (
  <span
    className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${
      status === "active"
        ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5"
        : "border-nexus-amber/60 text-nexus-amber bg-nexus-amber/10"
    }`}
  >
    {status}
  </span>
);

function TempPasswordModal({ email, temp, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(temp);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel brackets p-5 w-full max-w-md" data-testid="temp-password-modal" onClick={(e) => e.stopPropagation()}>
        <span className="b1" /><span className="b2" />
        <div className="flex items-center gap-2 text-nexus-cyan"><KeyRound size={16} /><span className="panel-title">Temporary Password</span></div>
        <p className="text-nexus-text-2 text-sm mt-3">
          Share these credentials with <span className="text-nexus-text font-mono">{email}</span> manually. This password is shown <span className="text-nexus-amber">only once</span> — they'll be asked to change it on first sign-in.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <code data-testid="temp-password-value" className="flex-1 bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono text-nexus-cyan break-all">{temp}</code>
          <button data-testid="temp-password-copy" onClick={copy} className="border border-nexus-border p-2 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2">
            {copied ? <Check size={15} /> : <Copy size={15} />}
          </button>
        </div>
        <button data-testid="temp-password-close" onClick={onClose} className="mt-5 w-full border border-nexus-cyan/60 text-nexus-cyan py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-nexus-cyan/10">Done</button>
      </div>
    </div>
  );
}

function InviteModal({ onClose, onInvited }) {
  const [form, setForm] = useState({ email: "", name: "", role: "read-only" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await usersApi.invite(form.email, form.name, form.role);
      onInvited(res);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form className="panel brackets p-5 w-full max-w-sm" data-testid="invite-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <span className="b1" /><span className="b2" />
        <div className="flex items-center gap-2 text-nexus-cyan"><UserPlus size={16} /><span className="panel-title">Invite Operator</span></div>
        <label className="block mt-4"><span className="panel-title">Email</span>
          <input data-testid="invite-email-input" type="email" required autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" /></label>
        <label className="block mt-3"><span className="panel-title">Full name</span>
          <input data-testid="invite-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" /></label>
        <label className="block mt-3"><span className="panel-title">Role</span>
          <select data-testid="invite-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select></label>
        {err && <div data-testid="invite-error" className="mt-3 text-[11px] font-mono text-nexus-rose border border-nexus-rose/40 bg-nexus-rose/5 px-2 py-1.5">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 border border-nexus-border text-nexus-text-2 py-2 text-[11px] font-mono uppercase tracking-widest hover:text-nexus-text">Cancel</button>
          <button data-testid="invite-submit" type="submit" disabled={busy} className="flex-1 border border-nexus-cyan/60 text-nexus-cyan py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-nexus-cyan/10 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={12} className="animate-spin" />} Send Invite
          </button>
        </div>
      </form>
    </div>
  );
}

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [temp, setTemp] = useState(null); // { email, temp_password }
  const [pendingRole, setPendingRole] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await usersApi.list();
      setUsers(d.items);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = users.filter((u) => u.status === "pending");
  const active = users.filter((u) => u.status !== "pending");

  const doApprove = async (u) => {
    setBusyId(u.user_id);
    try { await usersApi.approve(u.user_id, pendingRole[u.user_id] || "read-only"); await load(); }
    catch (e) { setError(formatApiError(e)); } finally { setBusyId(null); }
  };
  const doSetRole = async (u, role) => {
    setBusyId(u.user_id);
    try { await usersApi.setRole(u.user_id, role); await load(); }
    catch (e) { setError(formatApiError(e)); await load(); } finally { setBusyId(null); }
  };
  const doReset = async (u) => {
    setBusyId(u.user_id);
    try { const r = await usersApi.resetPassword(u.user_id); setTemp({ email: u.email, temp_password: r.temp_password }); }
    catch (e) { setError(formatApiError(e)); } finally { setBusyId(null); }
  };
  const doRemove = async (u) => {
    setBusyId(u.user_id);
    try { await usersApi.remove(u.user_id); setRemoveTarget(null); await load(); }
    catch (e) { setError(formatApiError(e)); setRemoveTarget(null); } finally { setBusyId(null); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="user-management-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title"><span className="dot" />Access Control</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            User <span className="text-nexus-cyan">Management</span>
          </h1>
          <p className="text-nexus-text-2 text-sm mt-1">Approve access requests, invite operators, and assign roles. Single-tenant RBAC — <span className="text-octopi-purple">admin</span> (full) · <span className="text-nexus-cyan">read-only</span> (view + query).</p>
        </div>
        <button data-testid="users-invite-button" onClick={() => setShowInvite(true)}
          className="flex items-center gap-2 border border-nexus-cyan/60 text-nexus-cyan px-4 py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-nexus-cyan/10">
          <UserPlus size={14} /> Invite Operator
        </button>
      </div>

      {error && <div data-testid="users-error" className="text-[11px] font-mono text-nexus-rose border border-nexus-rose/40 bg-nexus-rose/5 px-3 py-2">{error}</div>}

      {loading ? (
        <div className="text-nexus-text-2 font-mono text-xs flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading users…</div>
      ) : (
        <>
          {/* Pending approval queue */}
          {pending.length > 0 && (
            <div className="panel brackets p-0 overflow-hidden" data-testid="pending-section">
              <span className="b1" /><span className="b2" />
              <div className="px-4 py-2.5 border-b border-nexus-border flex items-center gap-2 text-nexus-amber">
                <Clock size={14} /><span className="panel-title">Pending Approval ({pending.length})</span>
              </div>
              <div className="divide-y divide-nexus-border">
                {pending.map((u) => (
                  <div key={u.user_id} data-testid={`pending-row-${u.user_id}`} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-nexus-text text-sm truncate">{u.name || "—"}</div>
                      <div className="text-nexus-text-3 text-[11px] font-mono truncate">{u.email} · {u.auth_provider} · {u.created_at ? fmtTimeAgo(u.created_at) : ""} ago</div>
                    </div>
                    <select data-testid={`pending-role-${u.user_id}`} value={pendingRole[u.user_id] || "read-only"} onChange={(e) => setPendingRole({ ...pendingRole, [u.user_id]: e.target.value })}
                      className="bg-nexus-bg border border-nexus-border px-2 py-1.5 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none">
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button data-testid={`approve-${u.user_id}`} disabled={busyId === u.user_id} onClick={() => doApprove(u)}
                      className="flex items-center gap-1.5 border border-emerald-500/60 text-emerald-400 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest hover:bg-emerald-500/10 disabled:opacity-50">
                      <ShieldCheck size={12} /> Approve
                    </button>
                    <button data-testid={`reject-${u.user_id}`} disabled={busyId === u.user_id} onClick={() => setRemoveTarget(u)}
                      className="flex items-center gap-1.5 border border-nexus-rose/50 text-nexus-rose px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest hover:bg-nexus-rose/10 disabled:opacity-50">
                      <X size={12} /> Reject
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active users */}
          <div className="panel p-0 overflow-hidden" data-testid="users-table">
            <div className="px-4 py-2.5 border-b border-nexus-border flex items-center gap-2 text-nexus-text-2">
              <Users size={14} /><span className="panel-title">Operators ({active.length})</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-nexus-text-3 border-b border-nexus-border">
                  <th className="px-4 py-2 font-normal">Operator</th>
                  <th className="px-4 py-2 font-normal">Provider</th>
                  <th className="px-4 py-2 font-normal">Status</th>
                  <th className="px-4 py-2 font-normal">Role</th>
                  <th className="px-4 py-2 font-normal text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {active.map((u) => {
                  const isSelf = u.user_id === me?.user_id;
                  return (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-nexus-border/60 hover:bg-nexus-panel-2/30">
                      <td className="px-4 py-3">
                        <div className="text-nexus-text truncate flex items-center gap-2">{u.name || "—"} {isSelf && <span className="text-[9px] font-mono text-nexus-text-3 border border-nexus-border px-1">YOU</span>}</div>
                        <div className="text-nexus-text-3 text-[11px] font-mono truncate">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-nexus-text-2 text-[11px] font-mono uppercase">{u.auth_provider}</td>
                      <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                      <td className="px-4 py-3">
                        {isSelf ? <RoleBadge role={u.role} /> : (
                          <select data-testid={`role-select-${u.user_id}`} value={u.role} disabled={busyId === u.user_id} onChange={(e) => doSetRole(u, e.target.value)}
                            className="bg-nexus-bg border border-nexus-border px-2 py-1 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none disabled:opacity-50">
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`reset-pw-${u.user_id}`} disabled={busyId === u.user_id} onClick={() => doReset(u)} title="Reset password"
                            className="flex items-center gap-1 border border-nexus-border px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 hover:border-nexus-amber hover:text-nexus-amber disabled:opacity-50">
                            <KeyRound size={11} /> Reset
                          </button>
                          {!isSelf && (
                            <button data-testid={`remove-${u.user_id}`} disabled={busyId === u.user_id} onClick={() => setRemoveTarget(u)} title="Remove user"
                              className="flex items-center gap-1 border border-nexus-rose/40 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-nexus-rose hover:bg-nexus-rose/10 disabled:opacity-50">
                              <Trash2 size={11} /> Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={(res) => { setShowInvite(false); setTemp({ email: res.user.email, temp_password: res.temp_password }); load(); }}
        />
      )}
      {temp && <TempPasswordModal email={temp.email} temp={temp.temp_password} onClose={() => setTemp(null)} />}

      {removeTarget && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setRemoveTarget(null)}>
          <div className="panel brackets p-5 w-full max-w-sm" data-testid="remove-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <span className="b1" /><span className="b2" />
            <div className="flex items-center gap-2 text-nexus-rose"><Trash2 size={16} /><span className="panel-title">Remove Access</span></div>
            <p className="text-nexus-text-2 text-sm mt-3">Permanently remove <span className="text-nexus-text font-mono">{removeTarget.email}</span>? Their sessions will be revoked immediately.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setRemoveTarget(null)} className="flex-1 border border-nexus-border text-nexus-text-2 py-2 text-[11px] font-mono uppercase tracking-widest hover:text-nexus-text">Cancel</button>
              <button data-testid="remove-confirm-button" disabled={busyId === removeTarget.user_id} onClick={() => doRemove(removeTarget)}
                className="flex-1 border border-nexus-rose/60 text-nexus-rose py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-nexus-rose/10 disabled:opacity-50 flex items-center justify-center gap-2">
                {busyId === removeTarget.user_id && <Loader2 size={12} className="animate-spin" />} Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
